import { z } from "zod";

import { PrincipalType } from "@/shared/principal";

/**
 * Client twin of the backend's `roboto.query.filters`: the filter wire model —
 * row variants, comparator/preset vocabulary, and the `filterSchema` validator.
 * The backend is the authoritative owner; `filtersParity.test.ts` checks this
 * module against the manifest the Python models generate. UI-side policy (which
 * operators a field OFFERS) lives with `FilterDefinition` in
 * `components/ResourceView/Filters`, not here.
 */

export type FilterDataType =
  | "string"
  | "numeric"
  | "date"
  | "boolean"
  | "set"
  | "enum"
  // An identity-valued field (`created_by` / `modified_by`). Its value is a
  // fully-qualified principal string (`user:<id>` / `device:<id>@<org>` /
  // `invocation:<id>`), and the principal TYPE is the operator: `IS_USER` picks
  // specific users, `IS_ANY_USER` matches every one of them.
  | "identity"
  // A user-created metric (dot-delimited path). Renders like `numeric` in the UI
  // (same comparators, value input, chip), but its `field` is a BARE path that
  // `filterToCondition` rewrites to `metric.<field>` — that prefixing is why the
  // variant exists. It's also distinct so a metric is distinguishable from a
  // numeric property (e.g. click-to-edit opens the metrics picker, not the form).
  | "metric";

/**
 * How distinct property filters join in the wire condition. `"AND"` (the
 * default) requires every filter to match; `"OR"` requires at least one.
 *
 * Lives in the filter schema (alongside `Filter`) rather than on the table
 * primitive because it's a filter concept, not a table concept.
 */
export type FilterMatchMode = "AND" | "OR";

// Value-bearing comparators per filter type — the ones that take an operand.
// The presence pair (IS_NOT_NULL / IS_NULL) is omitted here on purpose.
const stringValueComparators = [
  "EQUALS",
  "NOT_EQUALS",
  "CONTAINS",
  "NOT_CONTAINS",
  "LIKE",
  "NOT_LIKE",
] as const;

const numericValueComparators = [
  "EQUALS",
  "NOT_EQUALS",
  "GREATER_THAN",
  "LESS_THAN",
  "GREATER_THAN_OR_EQUAL",
  "LESS_THAN_OR_EQUAL",
] as const;

const dateValueComparators = ["LESS_THAN", "GREATER_THAN", "BETWEEN"] as const;

const booleanValueComparators = ["EQUALS"] as const;

// Single-valued, closed-option-set fields (a genuine enum, and custom Enum fields).
const enumValueComparators = ["EQUALS", "NOT_EQUALS"] as const;

// Identity fields: the principal TYPE is the operator, and the two operators a
// type contributes split along the same comparator/preset seam every other
// field type uses.
//
// `IS_<TYPE>` is a value-bearing COMPARATOR: it makes the user pick which
// entities of that type to match, and a multi-select fans out to an OR of
// EQUALS.
const identityValueComparators = [
  "IS_USER",
  "IS_DEVICE",
  "IS_INVOCATION",
  "IS_INTEGRATION",
  "IS_ORG",
] as const;

// `IS_ANY_<TYPE>` is a PRESET: valueless, with the comparator alone carrying
// the predicate, expanded at query-emit time (to a `<type>:%` LIKE prefix in
// `filterToCondition`) exactly like the date relatives. Registering it as one
// rather than hand-listing it in `VALUELESS_COMPARATORS` is what makes it
// valueless, renders it in the picker's preset row, and keeps `isPreset` true
// for it.
export const identityPresets = [
  "IS_ANY_USER",
  "IS_ANY_DEVICE",
  "IS_ANY_INVOCATION",
  "IS_ANY_INTEGRATION",
  "IS_ANY_ORG",
] as const;

// The full identity vocabulary — both halves, for the wire schema. Which of
// these a given field actually offers is decided per-definition by
// `effectiveComparators` / `effectivePresets` from the types it declares, so
// this is never any one field's menu.
//
// No presence axis: `created_by` / `modified_by` are non-null on the tables
// they target, so — like `set` — there is nothing to layer on per-field.
export const identityComparators = [
  ...identityValueComparators,
  ...identityPresets,
] as const;

export type IdentityComparator = (typeof identityComparators)[number];

/** The comparator / preset pair a single principal type contributes. */
export interface IdentityOperatorPair {
  /** The value-bearing `IS_<TYPE>` comparator. */
  comparator: IdentityComparator;
  /** The valueless `IS_ANY_<TYPE>` preset. */
  preset: IdentityComparator;
}

/**
 * The operator pair each principal type contributes. Exhaustive over
 * {@link PrincipalType} so a new platform principal type fails to compile
 * here until it gets operators, rather than silently becoming unfilterable.
 * Exported for the operator-picker policy in `FilterSchema.ts`, which narrows
 * an identity field's menu to the principal types it declares.
 */
export const IDENTITY_OPERATORS_BY_TYPE: Record<
  PrincipalType,
  IdentityOperatorPair
> = {
  user: { comparator: "IS_USER", preset: "IS_ANY_USER" },
  device: {
    comparator: "IS_DEVICE",
    preset: "IS_ANY_DEVICE",
  },
  invocation: {
    comparator: "IS_INVOCATION",
    preset: "IS_ANY_INVOCATION",
  },
  integration: {
    comparator: "IS_INTEGRATION",
    preset: "IS_ANY_INTEGRATION",
  },
  org: { comparator: "IS_ORG", preset: "IS_ANY_ORG" },
};

/** What an identity comparator means, once decoded. */
export interface IdentityOperator {
  /** The principal type the operator selects. */
  type: PrincipalType;
  /**
   * `true` for the `IS_ANY_<TYPE>` preset: matches every principal of `type`
   * via the `<type>:%` LIKE prefix, so the operator carries the whole predicate
   * and takes no value.
   */
  matchesAnyOfType: boolean;
}

// Inverted from `IDENTITY_OPERATORS_BY_TYPE` rather than written out again,
// so the two directions can't disagree.
const IDENTITY_OPERATORS: Record<IdentityComparator, IdentityOperator> = (
  Object.entries(IDENTITY_OPERATORS_BY_TYPE) as [
    PrincipalType,
    IdentityOperatorPair,
  ][]
).reduce(
  (operators, [type, pair]) => {
    operators[pair.comparator] = { type, matchesAnyOfType: false };
    operators[pair.preset] = { type, matchesAnyOfType: true };
    return operators;
  },
  {} as Record<IdentityComparator, IdentityOperator>,
);

/**
 * Decode an identity comparator into the principal type it targets and whether
 * it matches that type wholesale. Returns `undefined` for any other comparator,
 * so callers holding an umbrella {@link FilterComparator} can use it as a guard.
 */
export function identityOperator(
  comparator: FilterComparator,
): IdentityOperator | undefined {
  return IDENTITY_OPERATORS[comparator as IdentityComparator];
}

// Presence checks ("is set" / "is not set") shared by every scalar type that
// has a nullability axis. Valueless — the comparator alone carries the semantic.
// Layered onto a field's set by `effectiveComparators`, only when the field is `nullable`.
// Order is a UI choice (affirmative first).
export const presenceComparators = ["IS_NOT_NULL", "IS_NULL"] as const;

// Full per-type vocabulary: every comparator a type can carry (value-bearing +
// presence).
export const stringComparators = [
  ...stringValueComparators,
  ...presenceComparators,
] as const;

export const numericComparators = [
  ...numericValueComparators,
  ...presenceComparators,
] as const;

export const dateComparators = [
  ...dateValueComparators,
  ...presenceComparators,
] as const;

export const booleanComparators = [
  ...booleanValueComparators,
  ...presenceComparators,
] as const;

export const enumComparators = [
  ...enumValueComparators,
  ...presenceComparators,
] as const;

// Set-membership fields (e.g. tags) have no presence axis — the same
// CONTAINS / NOT_CONTAINS the backend's TAG_FIELD_COMPARATORS uses.
export const setComparators = ["CONTAINS", "NOT_CONTAINS"] as const;

/**
 * Preset comparators are a valueless UI convenience.
 */
export const datePresets = [
  "TODAY",
  "LAST_7_DAYS",
  "LAST_30_DAYS",
  "LAST_90_DAYS",
  "THIS_MONTH",
] as const;

export const comparatorsByType = {
  string: stringComparators,
  numeric: numericComparators,
  date: dateComparators,
  boolean: booleanComparators,
  set: setComparators,
  enum: enumComparators,
  identity: identityValueComparators,
  metric: numericComparators,
} as const satisfies Record<FilterDataType, readonly string[]>;

export const presetsByType = {
  string: [],
  numeric: [],
  date: datePresets,
  boolean: [],
  set: [],
  enum: [],
  identity: identityPresets,
  metric: [],
} as const satisfies Record<FilterDataType, readonly string[]>;

type ComparatorsFor<T extends FilterDataType> =
  | (typeof comparatorsByType)[T][number]
  | (typeof presetsByType)[T][number];

export type StringComparator = ComparatorsFor<"string">;
export type NumericComparator = ComparatorsFor<"numeric">;
export type DateComparator = ComparatorsFor<"date">;
export type BooleanComparator = ComparatorsFor<"boolean">;
export type SetComparator = ComparatorsFor<"set">;
export type EnumComparator = ComparatorsFor<"enum">;

export type FilterComparator = ComparatorsFor<FilterDataType>;

/**
 * Subtype of `DateComparator` corresponding to the relative presets — the
 * narrow form `filterToCondition` accepts when expanding to a live
 * `now`-relative range.
 */
export type DatePresetComparator = (typeof datePresets)[number];

/**
 * All preset comparators across every field type — derived from
 * `presetsByType` so adding/reordering presets only touches one place.
 * Presets are valueless and resolved at query-emit time: date relatives
 * expand to a `now`-relative range inside `filterToCondition` so a saved
 * filter stays live across days, and identity `IS_ANY_<TYPE>` expands to a
 * `<type>:%` LIKE prefix there too.
 */
export const PRESET_COMPARATORS: ReadonlySet<FilterComparator> = new Set(
  Object.values(presetsByType).flat(),
);

/**
 * Comparators that render no value input — the comparator alone carries
 * the semantic: `IS_NULL` / `IS_NOT_NULL` (the presence checks every
 * nullable scalar field can offer), plus every preset.
 */
export const VALUELESS_COMPARATORS: ReadonlySet<FilterComparator> = new Set([
  ...presenceComparators,
  ...PRESET_COMPARATORS,
]);

/** True iff `comparator` is a valueless preset (e.g. date `TODAY`). */
export function isPreset(comparator: FilterComparator): boolean {
  return PRESET_COMPARATORS.has(comparator);
}

/**
 * True iff `comparator` renders no value input — the comparator alone
 * carries the semantic (`IS_NULL` / `IS_NOT_NULL`, plus every preset).
 * Used by FilterForm to decide whether to hide the value input row.
 */
export function isValueless(comparator: FilterComparator): boolean {
  return VALUELESS_COMPARATORS.has(comparator);
}

/**
 * Narrow guard used by `filterToCondition` to dispatch into the
 * date-specific preset→range expander. Distinct from the generic
 * `isPreset` because preset expansion logic is type-specific (today only
 * dates) and needs the narrowed `DatePresetComparator` for exhaustiveness.
 */
export function isDatePreset(
  comparator: FilterComparator,
): comparator is DatePresetComparator {
  return (datePresets as readonly string[]).includes(comparator);
}

const stringVariant = z.object({
  type: z.literal("string"),
  field: z.string().min(1),
  comparator: z.enum(stringComparators),
  values: z.array(z.string()),
});

const numericVariant = z.object({
  type: z.literal("numeric"),
  field: z.string().min(1),
  comparator: z.enum(numericComparators),
  values: z.array(z.number()),
});

const dateVariant = z.object({
  type: z.literal("date"),
  field: z.string().min(1),
  comparator: z.enum([...dateComparators, ...datePresets]),
  values: z.array(z.string()),
});

const booleanVariant = z.object({
  type: z.literal("boolean"),
  field: z.string().min(1),
  comparator: z.enum(booleanComparators),
  values: z.array(z.boolean()),
});

// Option-backed filters (`set`, `enum`, `identity`) store the SELECTED
// OPTIONS — wire `value` plus the display `label` the user saw when they
// picked it (structurally `FilterDefinition`'s `LabeledOption`). One
// representation of value→label, owned by the committed filter itself: chips
// render `label` directly, and `filterToCondition` projects `value` at the
// wire boundary, so the label never reaches a `Condition`. For plain-string
// option sets, label === value.
const labeledOption = z.object({
  value: z.string(),
  label: z.string(),
});

const setVariant = z.object({
  type: z.literal("set"),
  field: z.string().min(1),
  comparator: z.enum(setComparators),
  values: z.array(labeledOption),
});

const enumVariant = z.object({
  type: z.literal("enum"),
  field: z.string().min(1),
  comparator: z.enum(enumComparators),
  values: z.array(labeledOption),
});

const identityVariant = z.object({
  type: z.literal("identity"),
  field: z.string().min(1),
  comparator: z.enum(identityComparators),
  // Labeled options like `set`/`enum`, so a picked principal carries its own
  // display name: `value` is the fully-qualified principal string
  // (`user:<id>`, `device:<id>@<org>`) the column stores, `label` the friendly
  // name the directory showed. Empty for the valueless `IS_ANY_<TYPE>` preset,
  // whose `<type>:%` pattern is derived from the operator at wire time.
  values: z.array(labeledOption),
});

// A metric filter is numeric-shaped; `field` holds the BARE dotted metric path
// (e.g. "navigation.path_deviation"). `filterToCondition` prepends the wire
// `metric.` prefix at conversion time.
const metricVariant = z.object({
  type: z.literal("metric"),
  field: z.string().min(1),
  comparator: z.enum(numericComparators),
  values: z.array(z.number()),
  unit: z.string().nullable().optional(),
});

export const filterSchema = z
  .discriminatedUnion("type", [
    stringVariant,
    numericVariant,
    dateVariant,
    booleanVariant,
    setVariant,
    enumVariant,
    identityVariant,
    metricVariant,
  ])
  .superRefine((data, ctx) => {
    switch (data.type) {
      case "string":
        if (isValueless(data.comparator)) {
          return;
        }
        if (
          data.values.length === 0 ||
          !data.values.some((v) => v.trim().length > 0)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "numeric":
      case "metric":
        if (isValueless(data.comparator)) {
          return;
        }
        if (
          data.values.length === 0 ||
          !data.values.every((n) => Number.isFinite(n))
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "date":
        // Relative presets and presence checks are both valueless here.
        if (isValueless(data.comparator)) {
          return;
        }
        if (data.comparator === "BETWEEN") {
          if (
            data.values.length !== 2 ||
            data.values.some((v) => v.length === 0)
          ) {
            ctx.addIssue({
              code: "custom",
              path: ["values"],
              message: "Start and end dates required",
            });
          } else if (data.values[0] > data.values[1]) {
            // ISO 8601 strings (UTC midnight) compare lexicographically,
            // so plain `>` is a correct date ordering check here.
            ctx.addIssue({
              code: "custom",
              path: ["values"],
              message: "End date must be on or after start date",
            });
          }
        } else if (data.values.length === 0 || data.values[0].length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Required",
          });
        }
        return;
      case "boolean":
        if (isValueless(data.comparator)) {
          return;
        }
        if (data.values.length !== 1) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick true or false",
          });
        }
        return;
      case "set":
        // Set fields don't support valueless comparators.
        if (data.values.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick at least one value",
          });
        }
        return;
      case "enum":
        if (isValueless(data.comparator)) {
          return;
        }
        if (data.values.length === 0) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick at least one value",
          });
        }
        return;
      case "identity":
        // The `IS_ANY_<TYPE>` preset needs no value — the operator is the
        // whole predicate.
        if (isValueless(data.comparator)) {
          return;
        }
        if (
          data.values.length === 0 ||
          !data.values.some((option) => option.value.trim().length > 0)
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["values"],
            message: "Pick an identity",
          });
        }
        return;
    }
  });

/**
 * The canonical UI Filter shape
 */
export type Filter = z.infer<typeof filterSchema>;

// The value-bearing comparators per type.
export const valueComparatorsByType = {
  string: stringValueComparators,
  numeric: numericValueComparators,
  // A metric is numeric-shaped: same value-bearing operators, and the presence pair
  // layered on by `effectiveComparators` like any other scalar type.
  metric: numericValueComparators,
  date: dateValueComparators,
  boolean: booleanValueComparators,
  enum: enumValueComparators,
  set: setComparators,
  identity: identityValueComparators,
} as const satisfies Record<FilterDataType, readonly FilterComparator[]>;
